import createDebug from 'debug';
import { Archive, Loader2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { LinkResponse, RelatedLink } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { useBatchOperationsMutation } from '@/hooks/useLinks';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

const debug = createDebug('app:related-sidebar');

interface RelatedLinksSidebarProps {
	currentLink: LinkResponse;
	relatedLinks: RelatedLink[];
	matchingTags: string[];
	onArchiveComplete: (archivedIds: number[]) => void;
	onNavigate?: (linkId: number) => void;
	onClose?: () => void;
	className?: string;
}

/**
 * Format a user tag for display.
 * e.g., "instagram:user:someuser" -> "@someuser"
 * e.g., "reddit:u:username" -> "u/username"
 * e.g., "reddit:r:subreddit" -> "r/subreddit"
 */
function formatUserTag(tag: string): string {
	// Reddit user/subreddit
	if (tag.startsWith('reddit:u:')) {
		return `u/${tag.replace('reddit:u:', '')}`;
	}
	if (tag.startsWith('reddit:r:')) {
		return `r/${tag.replace('reddit:r:', '')}`;
	}
	// Other platforms: extract username from "platform:user:username"
	const match = tag.match(/:user:(.+)$/);
	if (match) {
		return `@${match[1]}`;
	}
	return tag;
}

/**
 * Get platform icon for a tag
 */
function getPlatformIcon(tag: string): string {
	if (tag.startsWith('instagram:')) return '📷';
	if (tag.startsWith('twitter:')) return '𝕏';
	if (tag.startsWith('tiktok:')) return '♪';
	if (tag.startsWith('onlyfans:')) return '💙';
	if (tag.startsWith('fansly:')) return '💎';
	if (tag.startsWith('reddit:')) return '🔴';
	if (tag.startsWith('linktree:')) return '🌳';
	if (tag.startsWith('pornhub:')) return '🟠';
	if (tag.startsWith('redgifs:')) return '🔴';
	if (tag.startsWith('coomer:')) return '📦';
	return '🔗';
}

export function RelatedLinksSidebar({
	currentLink,
	relatedLinks,
	matchingTags,
	onArchiveComplete,
	onNavigate,
	onClose,
	className,
}: RelatedLinksSidebarProps) {
	const [isArchiving, setIsArchiving] = useState(false);
	const batchMutation = useBatchOperationsMutation();

	// Pre-select: current link (if not archived) + all non-archived related links
	const initialSelection = useMemo(() => {
		const selected = new Set<number>();
		if (!currentLink.archivedAt) {
			selected.add(currentLink.id);
		}
		for (const link of relatedLinks) {
			if (!link.archivedAt) {
				selected.add(link.id);
			}
		}
		return selected;
	}, [currentLink, relatedLinks]);

	const [selectedIds, setSelectedIds] = useState<Set<number>>(initialSelection);

	// Reset selection when current link changes
	useEffect(() => {
		setSelectedIds(initialSelection);
	}, [initialSelection]);

	// Get unique matching tags for display
	const uniqueTags = useMemo(() => {
		const tags = new Set<string>(matchingTags);
		for (const link of relatedLinks) {
			for (const tag of link.matchingTags) {
				tags.add(tag);
			}
		}
		return Array.from(tags);
	}, [matchingTags, relatedLinks]);

	function toggleSelection(id: number) {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}

	function selectAll() {
		const all = new Set<number>();
		if (!currentLink.archivedAt) all.add(currentLink.id);
		for (const link of relatedLinks) {
			if (!link.archivedAt) all.add(link.id);
		}
		setSelectedIds(all);
	}

	function selectNone() {
		setSelectedIds(new Set());
	}

	async function handleArchiveSelected() {
		const idsToArchive = Array.from(selectedIds);
		if (idsToArchive.length === 0) return;

		setIsArchiving(true);
		debug('archiving %d related links via GraphQL', idsToArchive.length);

		try {
			const result = await batchMutation.mutateAsync({ archive: idsToArchive });
			const archivedCount = result?.results?.archived ?? 0;
			debug('archived %d/%d links', archivedCount, idsToArchive.length);

			// Report archived IDs
			onArchiveComplete(idsToArchive);
		} catch (err) {
			debug('batch archive failed: %o', err);
		} finally {
			setIsArchiving(false);
		}
	}

	// Count stats
	const selectedCount = selectedIds.size;
	const unarchivedCount =
		(currentLink.archivedAt ? 0 : 1) + relatedLinks.filter((l) => !l.archivedAt).length;

	return (
		<div className={cn('flex flex-col h-full bg-background', className)}>
			{/* Header */}
			<div className="flex items-center justify-between border-b px-4 py-3">
				<div>
					<h2 className="font-semibold">Related Links</h2>
					<p className="text-xs text-muted-foreground">
						{selectedCount} of {unarchivedCount} selected
					</p>
				</div>
				{onClose && (
					<Button variant="ghost" size="icon" onClick={onClose}>
						<X className="size-4" />
					</Button>
				)}
			</div>

			{/* Matching Tags Pills */}
			{uniqueTags.length > 0 && (
				<div className="border-b px-4 py-2 flex flex-wrap gap-1">
					{uniqueTags.map((tag) => (
						<Badge key={tag} variant="secondary" className="text-xs">
							<span className="mr-1">{getPlatformIcon(tag)}</span>
							{formatUserTag(tag)}
						</Badge>
					))}
				</div>
			)}

			{/* Action Bar */}
			<div className="border-b p-3 flex items-center gap-2">
				<Button
					variant="destructive"
					size="sm"
					onClick={handleArchiveSelected}
					disabled={isArchiving || selectedIds.size === 0}
				>
					{isArchiving ? (
						<Loader2 className="size-4 animate-spin mr-1" />
					) : (
						<Archive className="size-4 mr-1" />
					)}
					Archive Selected
				</Button>
				<div className="flex-1" />
				<Button variant="ghost" size="sm" onClick={selectAll} disabled={isArchiving}>
					All
				</Button>
				<Button variant="ghost" size="sm" onClick={selectNone} disabled={isArchiving}>
					None
				</Button>
			</div>

			{/* Link List */}
			<ScrollArea className="flex-1">
				<div className="p-3 space-y-2">
					{/* Current Link (first, with visual distinction) */}
					<RelatedLinkItem
						link={{
							id: currentLink.id,
							url: currentLink.url,
							title: currentLink.title,
							thumbnailUrl: currentLink.thumbnailUrl,
							archivedAt: currentLink.archivedAt,
							matchingTags: matchingTags,
							createdAt: currentLink.createdAt,
						}}
						isCurrent={true}
						isSelected={selectedIds.has(currentLink.id)}
						onToggle={() => toggleSelection(currentLink.id)}
						disabled={isArchiving}
					/>

					{/* Divider */}
					{relatedLinks.length > 0 && (
						<div className="border-t my-2 pt-2">
							<p className="text-xs text-muted-foreground mb-2">
								{relatedLinks.length} related link{relatedLinks.length !== 1 ? 's' : ''}
							</p>
						</div>
					)}

					{/* Other Related Links */}
					{relatedLinks.map((link) => (
						<RelatedLinkItem
							key={link.id}
							link={link}
							isCurrent={false}
							isSelected={selectedIds.has(link.id)}
							onToggle={() => toggleSelection(link.id)}
							onNavigate={onNavigate ? () => onNavigate(link.id) : undefined}
							disabled={isArchiving}
						/>
					))}
				</div>
			</ScrollArea>
		</div>
	);
}

interface RelatedLinkItemProps {
	link: RelatedLink;
	isCurrent: boolean;
	isSelected: boolean;
	onToggle: () => void;
	onNavigate?: () => void;
	disabled?: boolean;
}

function RelatedLinkItem({
	link,
	isCurrent,
	isSelected,
	onToggle,
	onNavigate,
	disabled,
}: RelatedLinkItemProps) {
	const isArchived = link.archivedAt !== null;
	const canNavigate = !isCurrent && onNavigate;

	return (
		<div
			className={cn(
				'flex items-center gap-3 p-2 rounded transition-colors',
				isCurrent && 'bg-primary/10 border border-primary/20',
				isArchived && 'opacity-50',
				!isArchived && !isCurrent && 'hover:bg-muted/50',
				canNavigate && 'cursor-pointer'
			)}
			onClick={canNavigate ? onNavigate : undefined}
			onKeyDown={canNavigate ? (e) => e.key === 'Enter' && onNavigate() : undefined}
			role={canNavigate ? 'button' : undefined}
			tabIndex={canNavigate ? 0 : undefined}
		>
			{/* Checkbox - stop propagation so clicking it doesn't navigate */}
			<div onClick={(e) => e.stopPropagation()}>
				<Checkbox
					checked={isSelected}
					onCheckedChange={onToggle}
					disabled={disabled || isArchived}
				/>
			</div>

			{link.thumbnailUrl && (
				<img
					src={`/api/thumbnail/${link.id}`}
					alt=""
					className="size-10 rounded object-cover shrink-0"
					loading="lazy"
				/>
			)}

			<div className="flex-1 min-w-0">
				<div className="text-sm font-medium truncate flex items-center gap-2">
					<span className="truncate">{link.title || 'Untitled'}</span>
					{isCurrent && (
						<Badge variant="outline" className="text-[10px] shrink-0">
							Current
						</Badge>
					)}
				</div>
				<div className="text-xs text-muted-foreground truncate">
					{link.matchingTags.map(formatUserTag).join(', ')}
				</div>
			</div>

			{isArchived && (
				<Badge variant="secondary" className="text-[10px] shrink-0">
					Archived
				</Badge>
			)}
		</div>
	);
}
